import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import { Button, IconButton, Typography } from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import VpnKeyIcon from '@material-ui/icons/VpnKey';

import styles from '../styles';
import { theme } from 'config/theme';
import { PriceOptionForm } from './PriceOptionForm';
import { useAppSelector } from 'index';
import { actionCreators } from 'store/restaurantDetailsStore/actionCreators';
import { PricingLevelAccessState } from './PricingLevelsCard';
import { ManagedGroupPricing } from 'api/models';

const QRCode = require('qrcode.react');

interface AdvancedPriceOptionProps {
    id?: string;
    name?: string;
    disabled?: boolean;
    group?: ManagedGroupPricing;
    accessState?: PricingLevelAccessState;
    isAccessPanelOpen?: boolean;
    qrCanvasId?: string;
    onAccessPanelToggle?: (priceLevelId: string) => void;
    onGenerateAccess?: (priceLevelId: string) => void;
    onRegenerateAccess?: (priceLevelId: string) => void;
    onDownloadQr?: (priceLevelId: string, name: string) => void;
}

export function AdvancedPriceOption({
    name = i18n.t('common.standard'),
    id = '',
    disabled = false,
    group,
    accessState,
    isAccessPanelOpen = false,
    qrCanvasId = '',
    onAccessPanelToggle,
    onGenerateAccess,
    onRegenerateAccess,
    onDownloadQr,
}: AdvancedPriceOptionProps) {
    const classes = styles();
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const [edit, setEdit] = useState(false);
    const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

    const isGenerated = Boolean(accessState?.isGenerated);

    function getGeneratedDate() {
        if (!accessState?.generatedAt) return '';

        const locale = i18n.locale && i18n.locale.startsWith('de') ? 'de-CH' : 'en-GB';
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date(accessState.generatedAt));
    }

    function editOption(newName: string) {
        setEdit(false);
        dispatch(
            group
                ? actionCreators.updateGroupPricingLevel(restaurant.id, group.groupId, id, newName)
                : actionCreators.updatePricingLevel(restaurant.id, id, newName)
        );
    }

    function deleteOption() {
        setEdit(false);
        dispatch(
            group
                ? actionCreators.deleteGroupPricingLevel(restaurant.id, group.groupId, id)
                : actionCreators.deletePricingLevel(restaurant.id, id)
        );
    }

    function handleGenerate() {
        onGenerateAccess?.(id);
        setShowGenerateConfirm(false);
    }

    function handleRegenerate() {
        onRegenerateAccess?.(id);
        setShowRegenerateWarning(false);
    }

    const generatedDate = getGeneratedDate();

    return edit ? (
        <PriceOptionForm name={name} action={editOption} cancel={() => setEdit(false)} />
    ) : (
        <>
            <div className={classes.priceItemContainer}>
                <div className={classes.priceItemHeaderContainer}>
                    {name}
                    {!disabled && (
                        <div className={classes.priceItemActions}>
                            <IconButton
                                aria-label={i18n.t('common.edit')}
                                onClick={() => {
                                    setEdit(true);
                                }}
                            >
                                <CreateIcon htmlColor={theme.palette.secondary.dark} />
                            </IconButton>
                            <IconButton aria-label={i18n.t('common.delete')} onClick={deleteOption}>
                                <DeleteIcon htmlColor={theme.palette.secondary.dark} />
                            </IconButton>
                            <IconButton
                                aria-label={i18n.t('restaurant.pricingAccessKeyAction')}
                                onClick={() => onAccessPanelToggle?.(id)}
                            >
                                <span
                                    className={`${classes.keyIconWrapper} ${
                                        isGenerated ? classes.keyIconWrapperActive : ''
                                    }`}
                                >
                                    <VpnKeyIcon
                                        htmlColor={
                                            isGenerated
                                                ? theme.palette.primary.main
                                                : theme.palette.secondary.dark
                                        }
                                    />
                                    {isGenerated && <span className={classes.keyIconStatusDot} />}
                                </span>
                            </IconButton>
                        </div>
                    )}
                </div>

                {!disabled && isAccessPanelOpen && (
                    <>
                        {!isGenerated ? (
                            <div className={classes.notGeneratedContainer}>
                                <Typography className={classes.notGeneratedText}>
                                    {group
                                        ? i18n.t('restaurant.groupPricingAccessNotGeneratedCopy', {
                                              priceLevelName: name,
                                              groupName: group.groupName,
                                          })
                                        : i18n.t('restaurant.pricingAccessNotGeneratedCopy', {
                                              priceLevelName: name,
                                          })}
                                </Typography>
                                {group && showGenerateConfirm ? (
                                    <div className={classes.regenerateWarningActions}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleGenerate}
                                        >
                                            {i18n.t('restaurant.pricingAccessGenerateConfirm')}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            style={{
                                                color: theme.palette.secondary.dark,
                                                borderColor: theme.palette.secondary.dark,
                                                backgroundColor: theme.palette.common.white,
                                            }}
                                            onClick={() => setShowGenerateConfirm(false)}
                                        >
                                            {i18n.t('button.cancel')}
                                        </Button>
                                    </div>
                                ) : (
                                    <Button
                                        className={classes.notGeneratedActionButton}
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            group
                                                ? setShowGenerateConfirm(true)
                                                : onGenerateAccess?.(id)
                                        }
                                    >
                                        {i18n.t('restaurant.pricingAccessGenerate')}
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className={classes.accessPanelContainer}>
                                <div className={classes.generatedStateContainer}>
                                    <div className={classes.qrPreviewContainer}>
                                        <QRCode
                                            id={qrCanvasId}
                                            value={accessState?.registrationUrl || ''}
                                            size={100}
                                            renderAs="canvas"
                                        />
                                    </div>
                                    <div className={classes.generatedContentContainer}>
                                        <div className={classes.accessCodeChip}>
                                            {accessState?.accessCode || ''}
                                        </div>
                                        <Typography className={classes.generatedMetaText}>
                                            {generatedDate
                                                ? i18n.t('restaurant.pricingAccessGeneratedMeta', {
                                                      date: generatedDate,
                                                      priceLevelName: name,
                                                  })
                                                : i18n.t(
                                                      'restaurant.pricingAccessGeneratedMetaNoDate',
                                                      { priceLevelName: name }
                                                  )}
                                        </Typography>
                                        <div className={classes.generatedActionsRow}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => onDownloadQr?.(id, name)}
                                            >
                                                {i18n.t('restaurant.pricingAccessDownload')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                style={{
                                                    color: theme.palette.error.main,
                                                    borderColor: theme.palette.error.main,
                                                }}
                                                onClick={() => setShowRegenerateWarning(true)}
                                            >
                                                {i18n.t('restaurant.pricingAccessRegenerate')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                {showRegenerateWarning && (
                                    <div className={classes.regenerateWarningContainer}>
                                        <Typography className={classes.regenerateWarningText}>
                                            {group
                                                ? i18n.t(
                                                      'restaurant.groupPricingAccessRegenerateWarning',
                                                      {
                                                          accessCode: accessState?.accessCode || '',
                                                          groupName: group.groupName,
                                                      }
                                                  )
                                                : i18n.t(
                                                      'restaurant.pricingAccessRegenerateWarning',
                                                      { accessCode: accessState?.accessCode || '' }
                                                  )}
                                        </Typography>
                                        <div className={classes.regenerateWarningActions}>
                                            <Button
                                                variant="contained"
                                                style={{
                                                    backgroundColor: theme.palette.error.main,
                                                    color: theme.palette.common.white,
                                                }}
                                                onClick={handleRegenerate}
                                            >
                                                {i18n.t(
                                                    'restaurant.pricingAccessRegenerateConfirm'
                                                )}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                style={{
                                                    color: theme.palette.secondary.dark,
                                                    borderColor: theme.palette.secondary.dark,
                                                    backgroundColor: theme.palette.common.white,
                                                }}
                                                onClick={() => setShowRegenerateWarning(false)}
                                            >
                                                {i18n.t('button.cancel')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
